import { useEffect } from 'react';
import { getRoomPax } from '@koob/utils';
import useTripStore from '../../store/useTripStore';
import {
  matchDraftKeyRooms,
  matchPreviousNameRooms,
} from '../../logic/matchRoomSelection';

// The day fields a fresh auto-pick always clears once it runs, if they were
// set: a restored draft name list (consumed either way), and KD-3783's
// fallback flag (the room it asked to keep is picked, or replaced — either
// way the fallback it asked for happened).
function getPostSelectionReset(day: any): Record<string, any> | null {
  const patch: Record<string, any> = {};
  if (day?.previouslySelectedRoomNames) {
    patch.previouslySelectedRoomNames = null;
  }
  if (day?.hotelRoomFallsBackToCheapest) {
    patch.hotelRoomFallsBackToCheapest = false;
  }
  return Object.keys(patch).length ? patch : null;
}

// Split out of useTripDayHotelSelection (US-061) to stay under the package's
// max-lines-per-function cap. Auto-picks room(s) when availability data
// loads: tries to preserve the user's previous selection (a draft's room
// key, or previously selected room names), falls back to cheapest.
export function useAutoSelectHotelRooms({
  dayIndex,
  day,
  hotel,
  roomsComposition,
  isFirstInGroup,
  dayRoomSelection,
  roomsData,
}: {
  dayIndex: number;
  day: any;
  hotel: any;
  roomsComposition: any[];
  isFirstInGroup: boolean;
  dayRoomSelection: any;
  roomsData: any;
}) {
  const updateDay = useTripStore((state) => state.updateDay);
  const updateHotelRoomSelection = useTripStore(
    (state) => state.updateHotelRoomSelection,
  );

  // A room key restored from a draft/template was picked for whatever
  // composition was active when it was saved — e.g. a DMC template's own
  // 2-adult pick, restored as-is once the trip is re-seeded with the client
  // folder's 2 adults + 1 child. Guards the restore against the *current*
  // composition so a now-undersized room falls back to the cheapest fit
  // instead of being silently reused.
  const fitsCurrentComposition = (room: any, slotIndex: number) => {
    const slot = roomsComposition?.[slotIndex];
    const pax = getRoomPax(slot);
    if (!pax) return true;
    const min = room?.minCapacity ?? 0;
    const max = room?.maxCapacity ?? Infinity;
    return pax >= min && pax <= max;
  };

  useEffect(() => {
    if (
      !(
        roomsData?.possibilities &&
        roomsData?.cheapestRooms?.length > 0 &&
        roomsData?.roomCount === roomsComposition?.length &&
        isFirstInGroup &&
        hotel?.id
      )
    ) {
      return;
    }

    // Only auto-select if no room is currently selected for this day group
    if (dayRoomSelection) return;

    let roomsToSelect = roomsData.cheapestRooms;
    let roomKeysToSelect = roomsData.cheapestRoomsKeys;

    // 1. First priority: try to restore rooms from a draft (hotelSelectedRoomKey)
    if (day?.hotelSelectedRoomKey) {
      const result = matchDraftKeyRooms({
        hotelSelectedRoomKey: day.hotelSelectedRoomKey,
        roomsData,
        fitsCurrentComposition,
      });

      if (result.status === 'matched') {
        roomsToSelect = result.roomsToSelect;
        roomKeysToSelect = result.roomKeysToSelect;
      } else if (
        result.status === 'unavailable' &&
        !day?.hotelRoomFallsBackToCheapest
      ) {
        updateDay(dayIndex, {
          selectedRoom: {
            roomKey: day.hotelSelectedRoomKey,
            roomData: { name: result.missingRoomNames?.[0] },
            allRooms: result.missingRoomNames?.map((name) => ({ name })),
            isUnavailable: true,
            currency: hotel?.currency,
          },
          hotelSelectedRoomKey: null,
        });
        return;
      }
      // else 'fallthrough' (or 'unavailable' under the stay-date-change
      // fallback flag): keep the default cheapest-rooms selection
    }
    // 2. Second priority: try to restore previously selected rooms by name
    else if (day?.previouslySelectedRoomNames?.length > 0) {
      const result = matchPreviousNameRooms({
        previouslySelectedRoomNames: day.previouslySelectedRoomNames,
        roomsData,
        fitsCurrentComposition,
      });

      if (result.status === 'matched') {
        roomsToSelect = result.roomsToSelect;
        roomKeysToSelect = result.roomKeysToSelect;
      } else if (result.status === 'unavailable') {
        updateDay(dayIndex, {
          selectedRoom: {
            roomKey: null,
            roomData: { name: result.missingRoomNames?.[0] },
            allRooms: result.missingRoomNames?.map((name) => ({ name })),
            isUnavailable: true,
            currency: hotel?.currency,
          },
          previouslySelectedRoomNames: null,
        });
        return;
      }
      // else 'fallthrough': keep the default cheapest-rooms selection
    }

    updateHotelRoomSelection(
      hotel.id,
      {
        roomKey: roomKeysToSelect,
        composition: roomsToSelect[0], // Keep first room for compatibility
        allRooms: roomsToSelect,
        availabilityData: roomsData.possibilities,
        expiresAt: roomsData.expiresAt,
        requestId: roomsData.requestId,
      },
      dayIndex,
    );

    const resetPatch = getPostSelectionReset(day);
    if (resetPatch) updateDay(dayIndex, resetPatch);
    // Selects once per rooms answer: `day`, `roomsData` and the composition
    // check are read whole; re-running on each would re-select on every write.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    roomsData?.cheapestRoomsKeys,
    roomsData?.cheapestRooms,
    roomsData?.roomCount,
    roomsData?.possibilities,
    roomsData?.expiresAt,
    roomsComposition?.length,
    isFirstInGroup,
    hotel?.id,
    dayRoomSelection,
    day?.previouslySelectedRoomNames,
    day?.hotelSelectedRoomKey,
    day?.hotelRoomFallsBackToCheapest,
    dayIndex,
    updateHotelRoomSelection,
    updateDay,
  ]);
}
